import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo, faBookmark, faSearch, faTimes, faFolder, faTag } from "@fortawesome/free-solid-svg-icons";

import { AppLayout } from "../components/appLayout";
import useApiClient from "../hooks/useApiClient";
import {
    VideoViewModelPaginatedResultsViewModel,
    BookmarkViewModelPaginatedResultsViewModel,
    SearchVideosOptionsBindingModel,
    TagWithVideoCountViewModel,
    VideoViewModel,
    ToggleVideoTagBindingModel
} from "../apiClient";
import { VideoResults } from "../components/videoResults";
import { AppPagination } from "../components/appPagination";
import { BookmarkResults } from "../components/BookmarkResults";
import { AppSpinner } from "../components/appSpinner";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const parsePage = (value: string | null) => {
    const page = parseInt(value || '1', 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
};

export const Search = () => {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const apiClient = useApiClient();

    const query = (urlSearchParams.get('query') || '').trim();
    const categoryName = (urlSearchParams.get('categoryName') || '').trim();
    const tagsKey = urlSearchParams.getAll('tag').join('\u0000');
    const tagFilters = useMemo(() => (tagsKey ? tagsKey.split('\u0000') : []), [tagsKey]);
    const hasFilters = Boolean(query || categoryName || tagFilters.length > 0);
    // Tags only apply to videos, so bookmarks are only filtered by query/category.
    const hasBookmarkFilters = Boolean(query || categoryName);
    const videoPage = parsePage(urlSearchParams.get('videoPage'));
    const bookmarkPage = parsePage(urlSearchParams.get('bookmarkPage'));

    const [searchInput, setSearchInput] = useState(query);
    const [videoResults, setVideoResults] = useState<VideoViewModelPaginatedResultsViewModel | null>(null);
    const [bookmarkResults, setBookmarkResults] = useState<BookmarkViewModelPaginatedResultsViewModel | null>(null);
    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState<TagWithVideoCountViewModel[]>([]);

    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const requestRef = useRef(0);

    // Keep the input in sync with the URL (back/forward, navbar navigation),
    // without fighting the user while they are typing.
    useEffect(() => {
        setSearchInput(previous => (previous.trim() === query ? previous : query));
    }, [query]);

    // Focus the search field on arrival.
    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    // Let the navbar (or the global shortcut) focus the field when it is
    // already on the search page.
    useEffect(() => {
        const focusSearch = () => {
            inputRef.current?.focus();
            inputRef.current?.select();
        };

        window.addEventListener('vl:focus-search', focusSearch);
        return () => window.removeEventListener('vl:focus-search', focusSearch);
    }, []);

    // Cancel any pending debounce when leaving the page.
    useEffect(() => () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
    }, []);

    const fetchVideos = useCallback(async (page: number) => {
        if (!apiClient || !hasFilters) return null;
        const options = new SearchVideosOptionsBindingModel({
            query: query || undefined,
            categoryName: categoryName || undefined,
            tags: tagFilters.length > 0 ? tagFilters : undefined,
            page,
            pageSize: PAGE_SIZE
        });
        return apiClient.search(options);
    }, [apiClient, hasFilters, query, categoryName, tagFilters]);

    const fetchBookmarks = useCallback(async (page: number) => {
        if (!apiClient || !hasBookmarkFilters) return null;
        return apiClient.bookmarksGET(page, PAGE_SIZE, query || undefined, categoryName || undefined);
    }, [apiClient, hasBookmarkFilters, query, categoryName]);

    useEffect(() => {
        if (!hasFilters) {
            setVideoResults(null);
            setBookmarkResults(null);
            setLoading(false);
            return;
        }

        const requestId = ++requestRef.current;
        setLoading(true);

        Promise.all([fetchVideos(videoPage), fetchBookmarks(bookmarkPage)])
            .then(([videos, bookmarks]) => {
                if (requestId !== requestRef.current) return;
                setVideoResults(videos);
                setBookmarkResults(bookmarks);
            })
            .catch(error => {
                if (requestId === requestRef.current) {
                    console.error('Failed to load search results:', error);
                }
            })
            .finally(() => {
                if (requestId === requestRef.current) {
                    setLoading(false);
                }
            });
    }, [hasFilters, query, categoryName, tagFilters, videoPage, bookmarkPage, fetchVideos, fetchBookmarks]);

    const loadTags = useCallback(async () => {
        if (!apiClient) return;
        try {
            const tagsData = await apiClient.tagsAll();
            setTags(tagsData || []);
        } catch (error) {
            console.error('Failed to load tags:', error);
        }
    }, [apiClient]);

    useEffect(() => {
        loadTags();
    }, [loadTags]);

    const updateParams = useCallback((mutate: (params: URLSearchParams) => void, options?: { replace?: boolean }) => {
        setUrlSearchParams(previous => {
            const params = new URLSearchParams(previous);
            mutate(params);
            return params;
        }, options);
    }, [setUrlSearchParams]);

    const commitQuery = useCallback((value: string) => {
        const trimmed = value.trim();
        updateParams(params => {
            if (trimmed) {
                params.set('query', trimmed);
            } else {
                params.delete('query');
            }
            params.set('videoPage', '1');
            params.set('bookmarkPage', '1');
        }, { replace: true });
    }, [updateParams]);

    const removeTag = useCallback((tag: string) => {
        updateParams(params => {
            const remaining = params.getAll('tag').filter(existing => existing !== tag);
            params.delete('tag');
            remaining.forEach(existing => params.append('tag', existing));
            params.set('videoPage', '1');
            params.set('bookmarkPage', '1');
        });
    }, [updateParams]);

    const removeCategory = useCallback(() => {
        updateParams(params => {
            params.delete('categoryName');
            params.set('videoPage', '1');
            params.set('bookmarkPage', '1');
        });
    }, [updateParams]);

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchInput(value);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            if (value.trim() === query) return;
            commitQuery(value);
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        commitQuery(searchInput);
    };

    const handleClearSearch = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        setSearchInput('');
        commitQuery('');
        inputRef.current?.focus();
    };

    const handleSearchInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
            if (searchInput) {
                handleClearSearch();
            } else {
                event.currentTarget.blur();
            }
        }
    };

    const changePage = (key: 'videoPage' | 'bookmarkPage', page: number) => {
        const newParams = new URLSearchParams(urlSearchParams);
        newParams.set(key, page.toString());
        setUrlSearchParams(newParams);
    };

    const handleVideoPageChange = (page: number) => changePage('videoPage', page);
    const handleBookmarkPageChange = (page: number) => changePage('bookmarkPage', page);

    const handleVideoToggleTag = useCallback(async (video: VideoViewModel, bindingModel: ToggleVideoTagBindingModel) => {
        if (!apiClient) return;
        try {
            const updatedVideo = await apiClient.tags(video.id!, bindingModel);
            // Update the video in the results
            setVideoResults(prev => {
                if (!prev || !prev.results) return prev;
                const updatedResults = new VideoViewModelPaginatedResultsViewModel({ ...prev });
                const videoIndex = updatedResults.results!.findIndex(v => v.id === video.id);
                if (videoIndex !== -1) {
                    updatedResults.results![videoIndex] = updatedVideo;
                }
                return updatedResults;
            });
            // Reload tags to get updated counts
            loadTags();
        } catch (error) {
            console.error('Failed to toggle tag:', error);
        }
    }, [apiClient, loadTags]);

    const refreshVideos = useCallback(async () => {
        const videos = await fetchVideos(videoPage);
        if (videos) {
            setVideoResults(videos);
        }
    }, [fetchVideos, videoPage]);

    const videoTotal = videoResults?.totalCount ?? 0;
    const bookmarkTotal = bookmarkResults?.totalCount ?? 0;
    const totalResults = videoTotal + bookmarkTotal;
    const hasLoadedResults = videoResults !== null || bookmarkResults !== null;
    const showNoResults = hasFilters && !loading && hasLoadedResults && totalResults === 0;

    return (
        <AppLayout>
            <Container fluid className="search-page py-2">
                <div className="search-page__header">
                    <h1 className="h3 mb-1">Search</h1>
                    <p className="text-body-secondary mb-3">
                        Find videos and bookmarks across your library.
                    </p>

                    <Form onSubmit={handleSearchSubmit} className="search-page__form">
                        <InputGroup size="lg">
                            <InputGroup.Text>
                                <FontAwesomeIcon icon={faSearch} />
                            </InputGroup.Text>
                            <Form.Control
                                ref={inputRef}
                                type="text"
                                value={searchInput}
                                onChange={handleSearchInputChange}
                                onKeyDown={handleSearchInputKeyDown}
                                placeholder="Search videos, tags and bookmarks..."
                                aria-label="Search videos, tags and bookmarks"
                                autoFocus
                                className="search-input"
                            />
                            {searchInput && (
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    onClick={handleClearSearch}
                                    aria-label="Clear search"
                                    title="Clear search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                            )}
                            <Button variant="primary" type="submit" disabled={!searchInput.trim()}>
                                Search
                            </Button>
                        </InputGroup>
                    </Form>
                </div>

                {(categoryName || tagFilters.length > 0) && (
                    <div className="search-page__filters">
                        {categoryName && (
                            <span className="filter-chip">
                                <FontAwesomeIcon icon={faFolder} size="xs" />
                                <span>Category: {categoryName}</span>
                                <span
                                    role="button"
                                    className="filter-chip__remove"
                                    onClick={removeCategory}
                                    aria-label="Remove category filter"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </span>
                            </span>
                        )}
                        {tagFilters.map(tag => (
                            <span key={tag} className="filter-chip">
                                <FontAwesomeIcon icon={faTag} size="xs" />
                                <span>#{tag}</span>
                                <span
                                    role="button"
                                    className="filter-chip__remove"
                                    onClick={() => removeTag(tag)}
                                    aria-label={`Remove tag filter ${tag}`}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </span>
                            </span>
                        ))}
                    </div>
                )}

                {!hasFilters && (
                    <Card className="search-page__state text-center border-0">
                        <Card.Body className="py-5">
                            <div className="search-page__state-icon">
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            <h2 className="h4 mb-2">Search your library</h2>
                            <p className="text-body-secondary mb-1">Results update as you type.</p>
                            <p className="text-body-tertiary small mb-0">
                                Tip: press Ctrl/⌘ K anywhere to jump here.
                            </p>
                        </Card.Body>
                    </Card>
                )}

                {hasFilters && loading && !hasLoadedResults && (
                    <div className="text-center py-5">
                        <AppSpinner />
                        <p className="mt-3 text-body-secondary">Searching...</p>
                    </div>
                )}

                {showNoResults && (
                    <Card className="search-page__state text-center border-0">
                        <Card.Body className="py-5">
                            <div className="search-page__state-icon">
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            <h2 className="h4 mb-2">
                                {query ? <>No results for “{query}”</> : 'No results found'}
                            </h2>
                            <p className="text-body-secondary mb-0">
                                Try a different search term or clearing a filter.
                            </p>
                        </Card.Body>
                    </Card>
                )}

                {hasFilters && hasLoadedResults && !showNoResults && (
                    <>
                        <div className="search-page__summary">
                            <span>
                                {loading
                                    ? <>Searching{query ? <> for “{query}”</> : ''}…</>
                                    : <>{totalResults} result{totalResults === 1 ? '' : 's'}{query ? <> for “{query}”</> : ''}</>}
                            </span>
                            {loading && (
                                <Spinner animation="border" size="sm" role="status" aria-label="Loading results" />
                            )}
                        </div>

                        <Row className={loading ? 'search-page__results search-page__results--loading' : 'search-page__results'}>
                            <Col lg={6} className="mb-4">
                                <Card className="h-100">
                                    <Card.Header>
                                        <h3 className="h5 mb-0 d-flex align-items-center gap-2">
                                            <FontAwesomeIcon icon={faVideo} />
                                            Videos
                                            <Badge bg="secondary">{videoTotal}</Badge>
                                        </h3>
                                    </Card.Header>
                                    <Card.Body>
                                        {videoResults && (
                                            <>
                                                <VideoResults
                                                    paginatedResults={videoResults}
                                                    apiClient={apiClient!}
                                                    tags={tags}
                                                    onVideoUpdate={refreshVideos}
                                                    onVideoToggleTag={handleVideoToggleTag}
                                                    onPageChange={handleVideoPageChange}
                                                    showPagination={false}
                                                    columns={3}
                                                />
                                                {(videoResults.totalPages ?? 0) > 1 && (
                                                    <div className="mt-4">
                                                        <AppPagination
                                                            page={videoPage}
                                                            pageSize={PAGE_SIZE}
                                                            totalPages={Math.ceil(videoTotal / PAGE_SIZE)}
                                                            totalCount={videoTotal}
                                                            onPageChange={handleVideoPageChange}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={6} className="mb-4">
                                <Card className="h-100">
                                    <Card.Header>
                                        <h3 className="h5 mb-0 d-flex align-items-center gap-2">
                                            <FontAwesomeIcon icon={faBookmark} />
                                            Bookmarks
                                            <Badge bg="secondary">{bookmarkTotal}</Badge>
                                        </h3>
                                    </Card.Header>
                                    <Card.Body>
                                        {bookmarkResults && bookmarkTotal > 0 ? (
                                            <>
                                                <BookmarkResults bookmarks={bookmarkResults.results || []} />
                                                {(bookmarkResults.totalPages ?? 0) > 1 && (
                                                    <div className="mt-4">
                                                        <AppPagination
                                                            page={bookmarkPage}
                                                            pageSize={PAGE_SIZE}
                                                            totalPages={Math.ceil(bookmarkTotal / PAGE_SIZE)}
                                                            totalCount={bookmarkTotal}
                                                            onPageChange={handleBookmarkPageChange}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <p className="text-body-secondary text-center py-4 mb-0">
                                                {hasBookmarkFilters
                                                    ? 'No bookmarks found.'
                                                    : 'Search by term or category to filter bookmarks.'}
                                            </p>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
        </AppLayout>
    );
};
